import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OccEndpointsService } from '@spartacus/core';
import { Observable } from 'rxjs';
import { AreaOfUse, AreaOfUseEditParams, AreaOfUseList, DataImport } from '../../../core/model';
import { Occ } from '../../../core/occ';
import { ConverterService } from '../../../core/util';
import { AREA_OF_USE_CATEGORY_NORMALIZER } from './converters';

@Injectable({
  providedIn: 'root',
})
export class AreaOfUseAdminConnector {
  constructor(
    private httpClient: HttpClient,
    private occEndpointsService: OccEndpointsService,
    private converterService: ConverterService
  ) {}

  loadAreaOfUseList(userId: string): Observable<AreaOfUseList> {
    return this.httpClient.get<AreaOfUseList>(
      this.occEndpointsService.buildUrl('areasOfUseAdmin', {
        urlParams: { userId: userId },
      })
    );
  }

  loadProductAreasOfUse(userId: string, productCode: string): Observable<AreaOfUseList> {
    return this.httpClient.get<AreaOfUseList>(
      this.occEndpointsService.buildUrl('areasOfUseProduct', {
        urlParams: { userId, productCode },
      })
    );
  }

  addProductToAreaOfUse(userId: string, productCode: string, categoryCode: string): Observable<AreaOfUseList> {
    return this.httpClient.post<AreaOfUseList>(
      this.occEndpointsService.buildUrl('areaOfUseProductAdd', {
        urlParams: { userId, productCode, categoryCode },
      }),
      {}
    );
  }

  excludeArticlesFromAreaOfUse(userId: string, categoryCode: string, excludedArticleCodes: string[]): Observable<AreaOfUseList> {
    return this.httpClient.post<AreaOfUseList>(
      this.occEndpointsService.buildUrl('areaOfUseExcludeArticles', {
        urlParams: { userId, categoryCode },
      }),
      excludedArticleCodes
    );
  }

  removeProductsFromAreaOfUse(userId: string, productCodes: string[], categoryCode: string): Observable<AreaOfUseList> {
    const url = this.occEndpointsService.buildUrl('areaOfUseProductsRemove', {
      urlParams: { userId, categoryCode },
    });

    return this.httpClient.request('delete', url, {
      body: productCodes,
    });
  }

  createAreaOfUseCategory(userId: string, areaOfUseName: string, parentAreaOfUseCode?: string): Observable<AreaOfUse> {
    const url = this.occEndpointsService.buildUrl('areasOfUseAdmin', {
      urlParams: { userId },
      queryParams: { areaOfUseName, parentAreaOfUseCode },
    });
    return this.httpClient.post<Occ.AreaOfUse>(url, {}).pipe(this.converterService.pipeable(AREA_OF_USE_CATEGORY_NORMALIZER));
  }

  getAreaOfUseCategoryDetails(userId: string, code: string): Observable<AreaOfUse> {
    return this.httpClient.get(
      this.occEndpointsService.buildUrl('areaOfUseAdmin', {
        urlParams: {
          userId,
          code,
        },
      })
    );
  }

  deleteAreaOfUseCategory(userId: string, code: string): Observable<any> {
    return this.httpClient.delete(
      this.occEndpointsService.buildUrl('areaOfUseAdmin', {
        urlParams: {
          userId,
          code,
        },
      })
    );
  }

  editAreaOfUseCategory(userId: string, code: string, params: AreaOfUseEditParams): Observable<AreaOfUse> {
    return this.httpClient
      .patch(
        this.occEndpointsService.buildUrl('areaOfUseAdmin', {
          urlParams: {
            userId,
            code,
          },
          queryParams: {
            areaName: params.name,
            areaDescription: params.description,
            readyToPublish: params.readyToPublish,
            position: params.position,
          },
        }),
        {}
      )
      .pipe(this.converterService.pipeable(AREA_OF_USE_CATEGORY_NORMALIZER));
  }

  replaceAreaOfUseProduct(
    userId: string,
    categoryCode: string,
    oldProductCode: string,
    newProductCode: string
  ): Observable<AreaOfUse> {
    const url = this.occEndpointsService.buildUrl('areaOfUseProductReplace', {
      urlParams: { userId, categoryCode, oldProductCode, newProductCode },
    });
    return this.httpClient.put<Occ.OrderCard>(url, {});
  }

  publishAreasOfUse(userId: string): Observable<any> {
    const url = this.occEndpointsService.buildUrl('areasOfUseAdminPublish', {
      urlParams: { userId },
    });
    return this.httpClient.post(url, {});
  }

  // Export and Import
  getExportedAreaOfUseFile(userId: string, areaOfUseCode: string): Observable<Blob> {
    return this.httpClient.get(
      this.occEndpointsService.buildUrl('areaOfUseAdminDownload', {
        urlParams: { userId, areaOfUseId: areaOfUseCode },
      }),
      {
        responseType: 'blob',
      }
    );
  }

  sendImportAreaOfUseFile(userId: string, areaOfUseCode: string, file: File): Observable<DataImport> {
    const formData: FormData = new FormData();
    formData.append('file', file);
    return this.httpClient.post<DataImport>(
      this.occEndpointsService.buildUrl('areaOfUseAdminImport', {
        urlParams: {
          userId,
          areaOfUseId: areaOfUseCode,
        },
      }),
      formData
    );
  }

  downloadImportAreaOfUseFileTemplate(userId: string, areaOfUseCode: string): Observable<Blob> {
    return this.httpClient.get(
      this.occEndpointsService.buildUrl('areaOfUseAdminDownloadTemplate', { urlParams: { userId, areaOfUseId: areaOfUseCode } }),
      {
        responseType: 'blob',
      }
    );
  }
}
